/**
 * Wire shapes for the Documents library, mirroring the proxy-server `docs`
 * package (`proxy-server/internal/ontology/docs/domain.go`). Markdown stays
 * inline in Postgres; binaries store bytes per revision in Postgres
 * (`doc_revision_files`). Both share the same folder tree, append-only
 * revision history, and Postgres-held edit lease, so these types carry the
 * lease + revision-guard fields the editor needs — not just metadata.
 *
 * Fields stay snake_case (the document-templates precedent): the proxy is the authority
 * on names and nothing here needs a camelCase translation layer.
 */

/** Who authored a revision / holds the edit lease. */
export type DocAuthorKind = 'user' | 'agent'

/** One row from `doc_folders`. */
export interface DocFolder {
  id: string
  name: string
  path: string
  created_at: string
  updated_at: string
}

/** Review lifecycle on a document row (CHECK-constrained on the proxy). */
export type DocStatus = 'draft' | 'in_review' | 'approved' | 'published'

/**
 * One row from `docs`. Markdown keeps `content` inline (1 MiB proxy cap);
 * binaries leave it empty and point at a per-revision S3 object. `format`
 * is `md` for living markdown and the file extension (`xlsx`, `pdf`, …)
 * otherwise. `current_revision_id` is the save guard's expected value, and
 * the `lock_*` quartet is the edit lease — all four are absent from the
 * JSON when no lease is held.
 */
export interface Doc {
  id: string
  name: string
  path: string
  content: string
  format: string
  content_type: string
  size_bytes: number
  fill_data?: Record<string, unknown>
  status?: DocStatus | string
  document_date?: string
  source_template_id?: string | null
  current_revision_id?: string
  lock_token?: string
  lock_holder_kind?: DocAuthorKind
  lock_holder_name?: string
  lock_expires_at?: string
  anchor_node_ids: string[]
  created_at: string
  updated_at: string
}

/**
 * Living markdown vs a binary artifact. Empty / missing format is treated
 * as markdown so pre-widening rows still open in the editor.
 */
export function isMarkdownDoc(doc: Pick<Doc, 'format'>): boolean {
  const format = (doc.format ?? '').trim().toLowerCase()
  return format === '' || format === 'md'
}

/** Ingest review on a library doc that landed from unstructured ingest. */
export type DocIngestReviewStatus = 'processed' | 'needs_review'

/** Client-facing ingest sidecar for a Documents-library row. */
export interface DocIngestMeta {
  pipeline: string
  jobId?: string
  runName?: string
  originalFilename: string
  ingestedAt: string
  reviewStatus: DocIngestReviewStatus
}

export const INGESTED_LIBRARY_PREFIX = 'ingested'

/** The Ingested Documents folder (`ingested`) or any path under it. */
export function isIngestedLibraryPath(path: string | null | undefined): boolean {
  return (
    path === INGESTED_LIBRARY_PREFIX ||
    (typeof path === 'string' && path.startsWith(`${INGESTED_LIBRARY_PREFIX}/`))
  )
}

function readIngestFillData(
  fillData: Record<string, unknown> | undefined
): Record<string, unknown> {
  const ingest = fillData?.['ingest']
  return ingest !== null && typeof ingest === 'object' && !Array.isArray(ingest)
    ? (ingest as Record<string, unknown>)
    : {}
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined
}

function asIngestReviewStatus(value: unknown): DocIngestReviewStatus {
  return value === 'processed' ? 'processed' : 'needs_review'
}

/**
 * Derive ingest metadata for a library doc. Returns null unless the path
 * is under `ingested/`. Persisted `fill_data.ingest` wins; otherwise
 * pipeline/filename/timestamp fall back to unstructured + path + created_at.
 */
export function ingestMetaFromDoc(
  doc: Pick<Doc, 'path' | 'name' | 'created_at' | 'fill_data'>
): DocIngestMeta | null {
  if (!isIngestedLibraryPath(doc.path)) return null
  const ingest = readIngestFillData(doc.fill_data)
  const slash = doc.path.lastIndexOf('/')
  const leaf = slash >= 0 ? doc.path.slice(slash + 1) : doc.path
  const jobId = readOptionalString(ingest['job_id'])
  const runName = readOptionalString(ingest['run_name'])
  return {
    pipeline: readOptionalString(ingest['pipeline']) ?? 'unstructured',
    ...(jobId !== undefined ? { jobId } : {}),
    ...(runName !== undefined ? { runName } : {}),
    originalFilename: readOptionalString(ingest['original_filename']) ?? doc.name ?? leaf,
    ingestedAt: readOptionalString(ingest['ingested_at']) ?? doc.created_at,
    reviewStatus: asIngestReviewStatus(ingest['review_status'])
  }
}

/**
 * An in-browser preview rendering of a Documents-library binary. The BFF
 * converts docx/xlsx/csv to a script-free HTML body fragment (`html`); the
 * UI drops it into a sandboxed iframe. PDFs are not returned here — the UI
 * previews their revision bytes natively (`docRevisionObjectUrl`).
 */
export interface DocPreview {
  html: string
  format: string
}

/** One row from `doc_revisions` without its content payload. */
export interface DocRevisionMeta {
  id: string
  doc_id: string
  author_kind: DocAuthorKind
  author_name: string
  summary: string
  format?: string
  content_type?: string
  size_bytes?: number
  created_at: string
}

/** A revision plus the full markdown snapshot it captured. */
export interface DocRevision extends DocRevisionMeta {
  content: string
}

/** Body for `POST /doc-folders`. */
export interface CreateDocFolderInput {
  name: string
  path: string
}

/** Body for the name-only folder rename. */
export interface RenameDocFolderInput {
  name: string
}

/** Body for `POST /docs`. */
export interface CreateDocInput {
  name: string
  path: string
  content?: string
  anchor_node_ids?: string[]
}

/** Body for `PATCH /docs/{id}` — rename and/or move; at least one required. */
export interface UpdateDocInput {
  name?: string
  path?: string
}

/** Body for `POST /doc-folders/move`. */
export interface MoveDocFolderInput {
  fromPath: string
  toPath: string
  name?: string
}

export interface MoveDocFolderResponse {
  fromPath: string
  toPath: string
}

/** Body for `POST /doc-folders/copy`. */
export interface CopyDocFolderInput {
  fromPath: string
  toPath: string
  name?: string
}

export interface CopyDocFolderResponse {
  fromPath: string
  toPath: string
  copiedFolders: number
  copiedDocs: number
}

/** Body for `POST /docs/{id}/copy`. */
export interface CopyDocInput {
  name: string
  path: string
}

/**
 * Body for `PUT /docs/{id}/content`. The save is guarded twice: `lock_token`
 * must be a live lease and `expected_revision_id` must equal the doc's
 * `current_revision_id` (omitted for a doc with no revisions yet). A mismatch
 * is a 409 `DOC_REVISION_CONFLICT` carrying the current revision id and a
 * 4 KiB excerpt.
 */
export interface UpdateDocContentInput {
  content: string
  lock_token: string
  expected_revision_id?: string
  author_kind: DocAuthorKind
  author: string
  summary?: string
}

/** Body for the copy-forward restore; guarded exactly like a content save. */
export interface RestoreDocRevisionInput {
  lock_token: string
  expected_revision_id?: string
  author_kind: DocAuthorKind
  author: string
  summary?: string
}

/**
 * Body for `POST /docs/{id}:acquireLock`. Passing back the token the caller
 * already holds renews the lease — that is the heartbeat.
 */
export interface AcquireDocLockInput {
  holder_kind: DocAuthorKind
  holder_name: string
  lock_token?: string
}

/** Response of a successful acquire / renew. */
export interface AcquireDocLockResponse {
  lock_token: string
  lock_holder_kind: DocAuthorKind
  lock_holder_name: string
  lock_expires_at: string
  /** Proxy-suggested renew cadence (`DOCS_LOCK_HEARTBEAT`, default 30s). */
  heartbeat_seconds: number
  current_revision_id?: string
}

/** Args for the revision-history query (AIP-158 opaque page token). */
export interface ListDocRevisionsArgs {
  docId: string
  page_size?: number
  page_token?: string
}

/** One page of revision metadata. */
export interface DocRevisionPage {
  revisions: DocRevisionMeta[]
  next_page_token: string
}

/** One row from `doc_comments`. */
export interface DocComment {
  id: string
  doc_id: string
  parent_comment_id?: string | null
  author: string
  body: string
  created_at: string
  updated_at: string
}

/** Args for PATCH /docs/{id}/status. */
export interface UpdateDocStatusInput {
  status: DocStatus
}

/** Args for POST /docs/{id}/comments. */
export interface CreateDocCommentInput {
  body: string
  author?: string
  parent_comment_id?: string
}

/**
 * Wire shapes for document **templates**, mirroring the proxy-server
 * `report_template_*` Postgres rows (see
 * `proxy-server/internal/ontology/docs/templates/models.go`). HTTP stays
 * `/report-templates*`. Defined here rather than imported from
 * `@atbash-labs/proxy-client` to keep the browser bundle free of the
 * clients' Node-only (raw-fetch + `Buffer`) surface.
 */

/** One row from `report_template_folders`. */
export interface DocTemplateFolder {
  id: string
  name: string
  path: string
}

/** One row from `report_templates` (s3_key is internal, never serialized). */
export interface DocTemplate {
  id: string
  name: string
  path: string
  format: string
  content_type: string
  size_bytes: number
  created_at: string
  updated_at: string
}

/** Merge fields enumerated from a template's document bytes. */
export interface DocTemplateFields {
  fields: string[]
  format: string
}

/**
 * An in-browser preview rendering of a template document. The BFF
 * converts docx/xlsx to a script-free HTML body fragment (`html`); the UI
 * drops it into a sandboxed iframe. PDFs are not returned here — the UI
 * previews their bytes natively (see `docTemplateObjectUrl` in `download.ts`).
 */
export interface DocTemplatePreview {
  html: string
  format: string
}

/**
 * Upload response. Extends {@link DocTemplate} with the merge fields an
 * AI pass added at upload time (present only when `autoAddFields` was set and
 * fields were injected), so the UI can show what was created without a
 * follow-up fetch.
 */
export interface UploadedDocTemplate extends DocTemplate {
  /** Field names the AI injected. Present (possibly `[]`) when the pass ran. */
  aiFields?: string[]
  /** Set when the AI pass threw; the original bytes were stored unchanged. */
  aiError?: string
}

/** Body for the folder-create endpoints. */
export interface CreateDocTemplateFolderInput {
  name: string
  path: string
}

/** Body for the name-only folder rename endpoints. */
export interface RenameDocTemplateFolderInput {
  name: string
}

/**
 * Body for PATCH /report-templates/{id}.
 * Name and path are independently optional; at least one must be set.
 */
export interface PatchDocTemplateInput {
  name?: string
  path?: string
}

/** Body for POST /report-template-folders/move. */
export interface MoveDocTemplateFolderInput {
  fromPath: string
  toPath: string
  name?: string
}

export interface MoveDocTemplateFolderResponse {
  fromPath: string
  toPath: string
}

/** Body for POST /report-template-folders/copy. */
export interface CopyDocTemplateFolderInput {
  fromPath: string
  toPath: string
  name?: string
}

export interface CopyDocTemplateFolderResponse {
  fromPath: string
  toPath: string
  copiedFolders: number
  copiedItems: number
}

/** Body for POST /report-templates/{id}/copy. */
export interface CopyDocTemplateInput {
  name: string
  path: string
}

/** Args for uploading a document-template file. */
export interface UploadDocTemplateArgs {
  file: File
  /** Display name; defaults to the file name on the server when blank. */
  name?: string
  path: string
  /**
   * When true (PDF templates only), the server runs an LLM pass that adds
   * fillable AcroForm merge fields to the document before storing it.
   */
  autoAddFields?: boolean
}
